import { useEffect, useReducer, useState, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { chatReducer, CHAT_ACTIONS } from "../../reducers/chatReducer";
import { initializeSocket, getSocket, disconnectSocket } from "../../socket";
import {
  getConversations,
  getMessages,
  createOrGetConversation,
} from "../../functions/chat";
import ChatSidebar from "../../components/chat/ChatSidebar";
import ChatWindow from "../../components/chat/ChatWindow";
import CallInterface, {
  IncomingCallNotification,
} from "../../components/chat/CallInterface";
import ConferenceRoom, {
  ConferenceInviteModal,
} from "../../components/chat/ConferenceRoom";
import CreateConference from "../../components/chat/CreateConference";
import Header from "../../components/header";
import "./style.css";

const initialState = {
  conversations: [],
  activeConversation: null,
  messages: [],
  onlineUsers: [],
  typingUsers: {},
  unreadTotal: 0,
  loading: false,
  error: null,
};

export default function Chat() {
  const { user } = useSelector((state) => ({ ...state }));
  const { odIus } = useParams();
  const [state, dispatch] = useReducer(chatReducer, initialState);
  const [showSidebar, setShowSidebar] = useState(true);
  const socketRef = useRef(null);

  // Call states
  const [activeCall, setActiveCall] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);

  // Refs para evitar problemas de closure nos event listeners
  const activeCallRef = useRef(null);
  const activeConferenceRef = useRef(null);

  // Conference states
  const [activeConference, setActiveConference] = useState(null);
  const [showCreateConference, setShowCreateConference] = useState(false);
  const [conferenceInvite, setConferenceInvite] = useState(null);

  // Manter os refs sincronizados com o state
  useEffect(() => {
    activeCallRef.current = activeCall;
  }, [activeCall]);

  useEffect(() => {
    activeConferenceRef.current = activeConference;
  }, [activeConference]);

  // Initialize socket connection - runs only once when token is available
  useEffect(() => {
    if (user?.token) {
      socketRef.current = initializeSocket(user.token);

      // Socket event listeners
      socketRef.current.on("users:online", (users) => {
        dispatch({ type: CHAT_ACTIONS.SET_ONLINE_USERS, payload: users });
      });

      socketRef.current.on("user:online", (data) => {
        dispatch({ type: CHAT_ACTIONS.USER_ONLINE, payload: data.userId });
      });

      socketRef.current.on("user:offline", (data) => {
        dispatch({ type: CHAT_ACTIONS.USER_OFFLINE, payload: data.userId });
      });

      socketRef.current.on("message:new", (message) => {
        dispatch({ type: CHAT_ACTIONS.ADD_MESSAGE, payload: message });

        // Update conversation's last message
        dispatch({
          type: CHAT_ACTIONS.UPDATE_CONVERSATION,
          payload: {
            _id: message.conversationId,
            lastMessage: message,
            updatedAt: new Date().toISOString(),
          },
        });
      });

      socketRef.current.on("message:sent", (data) => {
        // Message sent confirmation
      });

      socketRef.current.on("error", (error) => {
        console.error("Socket error:", error);
      });

      socketRef.current.on("message:delivered", (data) => {
        // Update message delivery status
      });

      socketRef.current.on("message:read", (data) => {
        // Update message read status
      });

      socketRef.current.on("message:reacted", (data) => {
        dispatch({
          type: CHAT_ACTIONS.UPDATE_MESSAGE,
          payload: { _id: data.messageId, reactions: data.reactions },
        });
      });

      socketRef.current.on("message:deleted", (data) => {
        dispatch({ type: CHAT_ACTIONS.DELETE_MESSAGE, payload: data });
      });

      socketRef.current.on("typing:started", (data) => {
        dispatch({
          type: CHAT_ACTIONS.SET_TYPING,
          payload: {
            conversationId: data.conversationId,
            userName: data.userName,
          },
        });
      });

      socketRef.current.on("typing:stopped", (data) => {
        dispatch({
          type: CHAT_ACTIONS.CLEAR_TYPING,
          payload: data.conversationId,
        });
      });

      // Call event listeners - using refs to avoid closure issues
      socketRef.current.on("call:incoming", (data) => {
        console.log("[Chat] Incoming call received:", data);
        // Se já está em uma chamada, rejeitar automaticamente (usando ref)
        if (activeCallRef.current) {
          console.log("[Chat] Already in call, rejecting");
          socketRef.current.emit("call:reject", { callId: data.callId });
          return;
        }
        setIncomingCall(data);
      });

      socketRef.current.on("call:initiated", (data) => {
        // Confirmação de que a chamada foi iniciada
        console.log("[Chat] Call initiated:", data);
      });

      socketRef.current.on("call:missed", ({ callId }) => {
        console.log("[Chat] Call missed:", callId);
        setActiveCall((prev) => (prev?.callId === callId ? null : prev));
        setIncomingCall((prev) => (prev?.callId === callId ? null : prev));
      });

      socketRef.current.on("call:unavailable", ({ message }) => {
        console.log("[Chat] Call unavailable:", message);
        alert(message === "User is offline" ? "Usuário está offline" : message);
        setActiveCall(null);
      });

      socketRef.current.on("call:rejected", ({ callId }) => {
        console.log("[Chat] Call rejected:", callId);
        setActiveCall((prev) => (prev?.callId === callId ? null : prev));
      });

      socketRef.current.on("call:ended", ({ callId }) => {
        console.log("[Chat] Call ended:", callId);
        setActiveCall(null);
      });

      // Conference event listeners - using refs to avoid closure issues
      socketRef.current.on("conference:invite", (data) => {
        // Se já está em uma conferência, ignorar (usando ref)
        if (activeConferenceRef.current) return;
        setConferenceInvite(data);
      });

      socketRef.current.on("conference:updated", (data) => {
        setActiveConference((prev) => {
          if (prev && prev._id === data.conferenceId) {
            return { ...prev, ...data.updates };
          }
          return prev;
        });
      });

      socketRef.current.on("conference:ended", (data) => {
        setActiveConference((prev) => {
          if (prev && prev._id === data.conferenceId) {
            alert("A conferência foi encerrada pelo organizador");
            return null;
          }
          return prev;
        });
      });

      socketRef.current.on("conference:admitted", (data) => {
        // Usuário foi admitido na sala de espera
        if (data.conference) {
          setActiveConference(data.conference);
        }
      });

      return () => {
        disconnectSocket();
      };
    }
  }, [user?.token]); // Only depends on token - listeners use refs for current state

  // Load conversations
  useEffect(() => {
    const loadConversations = async () => {
      if (!user?.token) return;

      dispatch({ type: CHAT_ACTIONS.SET_LOADING, payload: true });
      try {
        const data = await getConversations(user.token);
        dispatch({ type: CHAT_ACTIONS.SET_CONVERSATIONS, payload: data });
      } catch (error) {
        dispatch({
          type: CHAT_ACTIONS.SET_ERROR,
          payload: error.response?.data?.message || "Failed to load conversations",
        });
      }
    };

    loadConversations();
  }, [user?.token]);

  // Handle direct chat from URL parameter
  useEffect(() => {
    const openDirectChat = async () => {
      if (odIus && user?.token) {
        try {
          const conversation = await createOrGetConversation(odIus, user.token);
          handleSelectConversation(conversation);
        } catch (error) {
          console.error("Error opening direct chat:", error);
        }
      }
    };

    openDirectChat();
  }, [odIus, user?.token]);

  // Select conversation handler
  const handleSelectConversation = useCallback(
    async (conversation) => {
      dispatch({
        type: CHAT_ACTIONS.SET_ACTIVE_CONVERSATION,
        payload: conversation,
      });

      // Join conversation room
      const socket = getSocket();
      if (socket) {
        socket.emit("conversation:join", conversation._id);
      }

      // Load messages
      dispatch({ type: CHAT_ACTIONS.SET_LOADING, payload: true });
      try {
        const data = await getMessages(conversation._id, 1, user.token);
        dispatch({ type: CHAT_ACTIONS.SET_MESSAGES, payload: data.messages });

        // Mark as read
        dispatch({
          type: CHAT_ACTIONS.MARK_AS_READ,
          payload: conversation._id,
        });
      } catch (error) {
        dispatch({
          type: CHAT_ACTIONS.SET_ERROR,
          payload: error.response?.data?.message || "Failed to load messages",
        });
      }

      // Hide sidebar on mobile
      if (window.innerWidth <= 768) {
        setShowSidebar(false);
      }
    },
    [user?.token]
  );

  // Send message handler
  const handleSendMessage = useCallback(
    (messageData) => {
      const socket = getSocket();
      if (socket && state.activeConversation) {
        const tempId = Date.now().toString();
        socket.emit("message:send", {
          ...messageData,
          conversationId: state.activeConversation._id,
          tempId,
        });
      }
    },
    [state.activeConversation]
  );

  // Typing handlers
  const handleTypingStart = useCallback(() => {
    const socket = getSocket();
    if (socket && state.activeConversation) {
      socket.emit("typing:start", {
        conversationId: state.activeConversation._id,
      });
    }
  }, [state.activeConversation]);

  const handleTypingStop = useCallback(() => {
    const socket = getSocket();
    if (socket && state.activeConversation) {
      socket.emit("typing:stop", {
        conversationId: state.activeConversation._id,
      });
    }
  }, [state.activeConversation]);

  // React to message handler
  const handleReact = useCallback((messageId, emoji) => {
    const socket = getSocket();
    if (socket) {
      socket.emit("message:react", { messageId, emoji });
    }
  }, []);

  // Delete message handler
  const handleDelete = useCallback((messageId, forEveryone) => {
    const socket = getSocket();
    if (socket) {
      socket.emit("message:delete", { messageId, forEveryone });
    }
  }, []);

  // Back to sidebar (mobile)
  const handleBackToSidebar = () => {
    setShowSidebar(true);
    dispatch({ type: CHAT_ACTIONS.SET_ACTIVE_CONVERSATION, payload: null });
  };

  // Iniciar chamada
  const handleStartCall = useCallback(
    (type) => {
      if (!state.activeConversation || activeCall) return;

      const socket = getSocket();
      if (!socket) return;

      // Obter o outro participante da conversa
      const otherUser = state.activeConversation.participants?.find(
        (p) => p._id !== user.id
      );

      if (!otherUser) {
        alert("Não foi possível encontrar o usuário");
        return;
      }

      const callData = {
        callId: null, // Será preenchido pelo servidor
        conversationId: state.activeConversation._id,
        type,
        targetUserId: otherUser._id,
        caller: {
          id: user.id,
          name: `${user.first_name} ${user.last_name}`,
          picture: user.picture,
        },
      };

      // Iniciar chamada via socket
      socket.emit("call:initiate", {
        conversationId: state.activeConversation._id,
        type,
        targetUserId: otherUser._id,
      });

      setActiveCall(callData);
    },
    [state.activeConversation, user, activeCall]
  );

  // Atender chamada
  const handleAnswerCall = useCallback(() => {
    if (!incomingCall) return;

    setActiveCall({
      ...incomingCall,
      isIncoming: true,
    });
    setIncomingCall(null);
  }, [incomingCall]);

  // Rejeitar chamada
  const handleRejectCall = useCallback(() => {
    if (!incomingCall) return;

    const socket = getSocket();
    if (socket) {
      socket.emit("call:reject", { callId: incomingCall.callId });
    }
    setIncomingCall(null);
  }, [incomingCall]);

  // Encerrar chamada
  const handleEndCall = useCallback(() => {
    setActiveCall(null);
  }, []);

  // Conference handlers
  const handleConferenceStart = useCallback((conference) => {
    setActiveConference(conference);
    setShowCreateConference(false);
  }, []);

  const handleConferenceEnd = useCallback(() => {
    setActiveConference(null);
  }, []);

  const handleAcceptConferenceInvite = useCallback(() => {
    if (conferenceInvite?.conference) {
      setActiveConference(conferenceInvite.conference);
      setConferenceInvite(null);
    }
  }, [conferenceInvite]);

  const handleDeclineConferenceInvite = useCallback(() => {
    setConferenceInvite(null);
  }, []);

  return (
    <div className="chat_page">
      <Header page="chat" />
      <div className="chat_container">
        <ChatSidebar
          conversations={state.conversations}
          activeConversation={state.activeConversation}
          onlineUsers={state.onlineUsers}
          typingUsers={state.typingUsers}
          onSelectConversation={handleSelectConversation}
          onOpenConference={() => setShowCreateConference(true)}
          visible={showSidebar}
          user={user}
        />
        <ChatWindow
          conversation={state.activeConversation}
          messages={state.messages}
          onlineUsers={state.onlineUsers}
          typingUsers={state.typingUsers}
          onSendMessage={handleSendMessage}
          onTypingStart={handleTypingStart}
          onTypingStop={handleTypingStop}
          onBack={handleBackToSidebar}
          visible={!showSidebar || state.activeConversation}
          user={user}
          loading={state.loading}
          onStartCall={handleStartCall}
          onReact={handleReact}
          onDelete={handleDelete}
        />
      </div>

      {/* Chamada ativa */}
      {activeCall && (
        <CallInterface
          callData={activeCall}
          user={user}
          onClose={handleEndCall}
          isIncoming={activeCall.isIncoming}
        />
      )}

      {/* Notificação de chamada recebida */}
      {incomingCall && !activeCall && (
        <IncomingCallNotification
          callData={incomingCall}
          onAnswer={handleAnswerCall}
          onReject={handleRejectCall}
        />
      )}

      {/* Modal para criar/entrar em conferência */}
      {showCreateConference && (
        <CreateConference
          user={user}
          onClose={() => setShowCreateConference(false)}
          onConferenceStart={handleConferenceStart}
        />
      )}

      {/* Sala de conferência ativa */}
      {activeConference && (
        <ConferenceRoom
          conference={activeConference}
          user={user}
          onClose={handleConferenceEnd}
        />
      )}

      {/* Convite para conferência */}
      {conferenceInvite && !activeConference && (
        <ConferenceInviteModal
          invite={conferenceInvite}
          onAccept={handleAcceptConferenceInvite}
          onDecline={handleDeclineConferenceInvite}
        />
      )}
    </div>
  );
}
